import React, { useEffect } from "react";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { WorkflowType } from "../../../../../../common/service/server/dto/RegulationRequestTypeDto";
import { Callout, CalloutVariant } from "../../../../Callout";
import { isConnectedSystemVendor } from "../../../../../util/vendorNesting";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";

export const CONNECTED_SYSTEM_AUTOMATION_MESSAGE =
  "TrueVault will communicate to your connected system to process this request.";

/**
 * Workflows a Connected System task is issued for, mirroring `RequestAutomationService`: the delete and opt-out
 * automations, and the access handoff added in POL-7757. Other workflows (GDPR restriction, objection, corrections)
 * never produce a Connected System outcome, so their rows keep the manual outcome controls.
 */
const CONNECTED_SYSTEM_AUTOMATED_WORKFLOWS: WorkflowType[] = [
  WorkflowType.DELETE,
  WorkflowType.OPT_OUT,
  WorkflowType.LIMIT,
  WorkflowType.PROFILING_OPT_OUT,
  WorkflowType.KNOW,
  WorkflowType.PORTABILITY,
];

/**
 * The TrueVault Connected System is always processed by automation, never by hand, so its row must not offer the
 * manual outcome controls (POL-7958). A request can still reach a workflow step before the Connected System has run -
 * most often when the recipient was added after the request came in, so the request has to be sent through automation -
 * and until an outcome is recorded the row shows {@link ConnectedSystemAutomationCallout} instead.
 */
export const isAwaitingConnectedSystemAutomation = (
  vendor: Pick<OrganizationDataRecipientDto, "vendorKey"> | null | undefined,
  outcome: string | null | undefined,
  request: Pick<DataSubjectRequestDetailDto, "dataRequestType"> | null | undefined,
): boolean =>
  isConnectedSystemVendor(vendor) &&
  !outcome &&
  CONNECTED_SYSTEM_AUTOMATED_WORKFLOWS.includes(request?.dataRequestType?.workflowType);

/** Read-only stand-in for the outcome controls, styled like the other automation outcome callouts. */
export const ConnectedSystemAutomationCallout = ({ className }: { className?: string }) => (
  <Callout
    variant={CalloutVariant.Gray}
    title={CONNECTED_SYSTEM_AUTOMATION_MESSAGE}
    className={className}
  />
);

/**
 * Without outcome controls the Connected System row cannot be completed by hand, so the step stays incomplete until
 * automation records an outcome. Explain that on the disabled "Next step" button rather than leaving the user guessing,
 * and point at the Automate button when it is available to them.
 */
export const useConnectedSystemAutomationBlock = (
  isAwaitingAutomation: boolean,
  allowManualAutomation: boolean,
) => {
  const { setNextStepBlockedReason } = useDataRequestProcessingContext();

  useEffect(() => {
    if (!isAwaitingAutomation) {
      return;
    }

    setNextStepBlockedReason(
      allowManualAutomation
        ? "Use Automate Processing to send this request to your Connected System"
        : "Waiting for your Connected System to process this request",
    );

    return () => setNextStepBlockedReason(null);
  }, [isAwaitingAutomation, allowManualAutomation, setNextStepBlockedReason]);
};
